import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	AlertColor,
	Box,
	Divider,
	Grid,
	Link as MuiLink,
	Stack,
	Typography
} from "@mui/material"
import React from "react"

import {
	Trans,
	t,
} from "@lingui/macro"
import { Link } from "react-router-dom"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"

import {
	AssetMatchBrandWithImage,
	AssetProvenience,
	CreateSpecificWorkflow,
	NewStepRequest,
	SessionResponse,
	WorkflowObjectType,
} from "models"

import {
	ErrorResponse,
	Loading,
	RestResponse,
} from "@sinossi/mates-react-library"
import { SbSnackbar } from "components/commons/sb-snackbar"
import noImage from "components/dashboard/dashboard-map/dashboard-map-stores/no-image-found-masema.png"
import { MatchProposalItemData } from "components/match-proposal/match-proposal-list/match-proposal-item-data"
import {
	MatesComponent,
	MatesComponentState,
} from "components/mates-component"
import { ActionsServices } from "services/actions.services"
import withUseRouter, { UseRouterParams } from "services/router"
import { WorkflowService } from "services/workflow.service"

export interface MatchProposalItemProps extends UseRouterParams {
	match: AssetMatchBrandWithImage

	sessionData: SessionResponse
}

export interface MatchProposalItemState extends MatesComponentState {
	snackbarData: {
		openNotification: boolean, notificationMessage: string, severity: AlertColor
	}
}

class MatchProposalItem extends React.Component<MatchProposalItemProps, MatchProposalItemState> {
	private readonly matesComponent: MatesComponent<MatchProposalItemProps, MatchProposalItemState> = new MatesComponent(
		this)

	constructor(props: MatchProposalItemProps) {
		super(props)

		this.state = {
			...this.matesComponent.setupState(),
			snackbarData: {
				openNotification: false,
				notificationMessage: "",
				severity: "info",
			},
		}
	}

	private getLogo = (): string => {
		switch (this.props.match.match.asset.provenience) {
			case AssetProvenience.IMMOBILIARE:
				return "/asset-provenience-logo/immobiliare.png"
			case AssetProvenience.CATASTO:
				return "/asset-provenience-logo/agenzia-del-territorio.png"
			case AssetProvenience.SELECTED_BY_STOREBOOK:
				return "/asset-provenience-logo/no-image-found-masema.png"
			case AssetProvenience.SUBITO:
				return "/asset-provenience-logo/subito.png"
			case AssetProvenience.IDEALISTA:
				return "/asset-provenience-logo/idealista.png"
			case AssetProvenience.FRONTEND:
				return ""
			default:
				return ""
		}
	}

	private createWorkflow = (templateId: string) => {
		if (this.props.match) {
			const request: CreateSpecificWorkflow = {
				objectId: this.props.match.match.id,
				objectType: WorkflowObjectType.MATCH,
				workflowTemplateId: templateId,
			}
			WorkflowService.createWorkflowFromSpecificObject(
				request,
				this.createWorkflowCallback,
				this.matesComponent.onError,
			)
		}
	}

	private createWorkflowCallback = (response: RestResponse<any, ErrorResponse>) => {
		if (response.hasError()) {
			this.openSnackbar(t({
				id: "asset-form.create-workflow.error",
				message: "Qualcosa è andato storto, riprova tra qualche istante.",
			}), "error")
			this.matesComponent.onError(response)
			return
		}
		this.openSnackbar(t({
			id: "asset-form.create-workflow.success",
			message: "Workflow creato con successo.",
		}), "success")
	}

	private createReminder = (request: NewStepRequest) => {
		ActionsServices.createNewAction(request, this.createReminderCallback, this.matesComponent.onError)
	}

	private createReminderCallback = (response: RestResponse<any, ErrorResponse>) => {
		if (response.hasError()) {
			this.matesComponent.onException(response)
			this.openSnackbar(t({
				id: "asset-form.create-reminder.error",
				message: "Qualcosa è andato storto, riprova tra qualche istante.",
			}), "error")
			return
		}

		this.openSnackbar(t({
			id: "asset-form.create-reminder.success",
			message: "Promemoria creato con successo.",
		}), "success")
	}

	private onScheduleVisitSuccess = (message: string) => {
		this.openSnackbar(message, "success")
	}

	private onScheduleVisitError = (error: any) => {
		this.matesComponent.onException(error)
		this.openSnackbar(t({
			id: "schedule-visit.error",
			message: "Errore durante la programmazione del sopralluogo"
		}), "error")
	}

	private openSnackbar = (message: string, severity: AlertColor) => {
		this.setState(() => ({
			snackbarData: {
				openNotification: true,
				notificationMessage: message,
				severity: severity,
			},
		}))
	}

	private closeSnackbar = () => {
		this.setState((prevState) => ({
			snackbarData: {
				...prevState.snackbarData,
				openNotification: false,
			},
		}))
	}


	render() {
		const { asset } = this.props.match.match
		const { proposta } = this.props.match.match

		let image: string | undefined = this.props.match.image
		let linkUrl: string = `/asset/${asset.id}`

		if ((image?.length || 0) === 0) {
			image = noImage
		}

		return (
			<>
				<Loading show={this.state.loading} />
				<Stack
					direction="row"
					sx={{
						mb: 2,
						fontSize: "8px",
						"&.MuiStack-root": {
							width: "100%"
						}
					}}
				>
					<Stack
						direction={"column"}
						sx={{
							"&.MuiStack-root": {
								width: "100%",
								borderRadius: "10px"
							}
						}}
					>
						<Stack
							direction="row"
							sx={{
								backgroundColor: "#f9fafb",
								borderRadius: "10px 10px 0 0",
								p: 2,
								"&.MuiStack-root": {
									width: "100%",
								}
							}}
						>
							<Box
								component="img"
								sx={{
									height: "48px",
									borderRadius: "10px",
									aspectRatio: 16 / 10,
									background: "#f9fafb",
									mr: 2,
								}}
								alt="Immagine Immobile"
								src={image}
							/>
							<Stack
								direction="row"
								sx={{
									height: "48px",
									justifyContent: "space-between",
									alignItems: "center",
									width: "100%",
									px: 1,
								}}
								spacing={2}
							>
								<Box
									sx={{ display: "flex", minWidth: "50%", gap: 3, alignItems: "center", }}
								>
									<Typography>
										{asset.address}
									</Typography>
									<MuiLink
										color="secondary"
										component={Link}
										to={linkUrl}
										fontSize="12px"
										padding={1}
										sx={{
											textDecoration: "none",
										}}
									>
										Vedi Scheda immobile
									</MuiLink>
								</Box>
								<Box
									sx={{ display: "flex", width: "40%", gap: 3, alignItems: "center", justifyContent: "right" }}
								>
									<Box
										sx={{ width: "100%", justifyContent: "left" }}
									>
										<Typography>
											{proposta ? (
												<>
													Proposta gestita da:{" "}
													<span style={{ color: "#00A3E5" }}>
														{proposta.accountManager!.accountDetails!.firstName}{" "}
														{proposta.accountManager!.accountDetails!.lastName}
													</span>
												</>
											) : (
												"/"
											)}
										</Typography>
									</Box>

									<Box sx={{ minWidth: "150px" }}>
										<Box
											component={"img"}
											src={this.getLogo()}
											sx={{
												height: "24px",
												display: "block",
											}}
										/>
									</Box>

								</Box>
							</Stack>
						</Stack>
						<Stack
							direction="row"
							spacing={1}
							sx={{
								backgroundColor: "white",
								px: 2,
								py: 0,
								paddingBottom: "1em",
								"&.MuiStack-root": {
									width: "100%"
								}
							}}
						>
							<Grid container rowSpacing={1} columnSpacing={2} alignItems={"center"}>
								<Grid item md={1}>
									<Typography fontWeight="bold" fontSize="0.8rem">
										<Trans id="mpi-brand">Brand</Trans>
									</Typography>
								</Grid>
								<Grid item md={2} />
								<Grid item md={1}>
									<Typography fontWeight="bold" fontSize="0.8rem">
										<Trans id="mpi-priority">Priorità</Trans>
									</Typography>
								</Grid>
								<Grid item md={1.5}>
									<Typography fontWeight="bold" fontSize="0.8rem">
										<Trans id="mpi-status">Stato</Trans>
									</Typography>
								</Grid>
								<Grid item md={2}>
									<Typography fontWeight="bold" fontSize="0.8rem">
										<Trans id="mpi-compatibility">Compatibilità</Trans>
									</Typography>
								</Grid>
								<Grid item md={2}>
									<Typography fontWeight="bold" fontSize="0.8rem">
										<Trans id="mpi-note">Note</Trans>
									</Typography>
								</Grid>
								<Grid item md={2.5}>

								</Grid>
								<Grid item md={12}>
									<Divider />
								</Grid>
								<MatchProposalItemData
									{...this.props}
									assetId={this.props.match.match.asset.id}
									matchId={this.props.match.match.id}
									logo={this.props.match.match.brand.logo}
									brandName={this.props.match.match.brand.name}
									priority={this.props.match.match.priorityMatch}
									compatibility={this.props.match.match.compatibility}
									status={this.props.match.match.status}
									notes={this.props.match.match.notes}
									rejectReason={this.props.match.match.rejectReason}
									rejectDescription={this.props.match.match.rejectDescription}
									createWorkflow={this.createWorkflow}
									createReminder={this.createReminder}
									onScheduleVisitSuccess={this.onScheduleVisitSuccess}
									onScheduleVisitError={this.onScheduleVisitError}
								/>
							</Grid>
						</Stack>
						<Accordion
							elevation={0}
							disabled={!this.props.match.allProposals.length}
							sx={{
								width: "100%",
								backgroundColor: "#F1F1F1",
								borderRadius: "0 0 10px 10px",
								"&.Mui-disabled": {
									backgroundColor: "#F1F1F1",
									color: "black"
								},
								"&.Mui-expanded": {
									margin: 0
								}
							}}
						>
							<AccordionSummary
								expandIcon={<ExpandMoreIcon />}
								aria-controls="additional-matches"
								id={`additional-matches-${this.props.match.match.asset.id}`}
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									border: 0,
									gap: 0,
									"&.Mui-expanded": {
										borderRadius: 0
									},
									"& .MuiAccordionSummary-content": {
										flexGrow: 0
									}
								}}
							>
								<Typography fontWeight="bold" fontSize="0.8rem">
									<Trans id={"mpi.more-match"}>
										Altre proposte per questo immobile: <span style={{ color: "#00A3E5" }}>{this.props.match.allProposals.length}</span>
									</Trans>
								</Typography>
							</AccordionSummary>
							<AccordionDetails
								sx={{
									background: "white",
									width: "100%",
									px: 2,
									py: 1
								}}
							>
								<Grid container spacing={2} alignItems={"center"} sx={{ m: 0, width: "100%" }}>
									{
										this.props.match.allProposals && this.props.match.allProposals
											.filter(proposal => proposal.id !== this.props.match.match.id) // Evita duplicazioni con il match principale
											.map((match, index, filteredArray) => (
												<React.Fragment key={match.id}>
													<MatchProposalItemData
														{...this.props}
														assetId={match.asset.id}
														matchId={match.id}
														logo={match.brand.logo}
														brandName={match.brand.name}
														priority={match.priorityMatch}
														compatibility={match.compatibility}
														status={match.status}
														notes={match.notes}
														rejectReason={match.rejectReason}
														rejectDescription={match.rejectDescription}
														createWorkflow={this.createWorkflow}
														createReminder={this.createReminder}
														onScheduleVisitSuccess={this.onScheduleVisitSuccess}
														onScheduleVisitError={this.onScheduleVisitError}
													/>
													{index < filteredArray.length - 1 && (
														<Grid item md={12} className={"grid-item-no-pt"}>
															<Divider />
														</Grid>
													)}
												</React.Fragment>
											))
									}
								</Grid>
							</AccordionDetails>
						</Accordion>
					</Stack>
				</Stack>
				<SbSnackbar snackbarData={this.state.snackbarData} handleCloseNotification={this.closeSnackbar} />
			</>)
	}
}

export default withUseRouter(MatchProposalItem)
